/**
 * 7-phase client setup. Hub is home after the package gate.
 *
 * First screen: package (SEO / Ads / Both). After that the hub. Each job
 * drills into a briefing card and returns here. Interruption returns here.
 * Go-live is this list with every required row done — quiet, no celebration.
 *
 * Copy never says Workspace, Client, Brand Bible, or KPI.
 *
 * Zero-tenant (no business yet): the same-browser zustand draft is the only
 * state — the package gate and setup cards run against it. After
 * createWorkspace, every write goes to the real /onboarding/* API and the
 * server status hydrates the draft back (draft becomes a cache).
 *
 * Existing clients (already_on_file, not mid-flow) go to the dashboard.
 * Zero-tenant users are not hung on "Getting things ready" — they see the gate.
 *
 * Contract: docs/research/onboarding/INTEGRATION-CONTRACT.md
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { useTenantStore } from '@/stores/useTenantStore';
import { useWorkspaceStore } from '@/stores/useWorkspaceStore';
import { fetchWorkspaces } from '@/services/authApi';
import { normalizeWebsite } from '@/lib/domain';
import {
  completeOnboarding,
  createBusiness,
  fetchOnboardingStatus,
  inviteTeammate,
  patchProgress,
  proveSite,
  savePackage,
  savePluginKey,
  saveSetup,
} from './onboardingService';
import {
  firstScreenForJob,
  isOnboardingScreen,
  type HubJob,
  type OnboardingScreen,
} from './jobs';
import {
  emptyDraft,
  PRE_TENANT_KEY,
  useOnboardingStore,
} from './useOnboardingStore';
import { OnboardingFrame, Eyebrow, Lede, StepBody } from './OnboardingShell';
import { PackageGate } from './screens/PackageGate';
import { Hub } from './screens/Hub';
import {
  BusinessInfo,
  Review,
  ServiceArea,
  Website,
} from './screens/SetupCards';
import {
  AdsLink,
  BaselineCrawl,
  Foundation,
  InviteTeam,
  ProveSite,
} from './screens/JobCards';
import type { ProductPackage } from '@/types/auth';

function resolveScreen(
  stepParam: string | undefined,
  pkg: ProductPackage | null,
): OnboardingScreen {
  if (!pkg) return 'gate';
  if (!stepParam) return 'hub';
  if (isOnboardingScreen(stepParam)) return stepParam;
  return 'hub';
}

/** Foundation, crawl, and ads stay locked until the site is proven.
 *  Setup and invite may run before the plugin. */
const PROVE_GATED_JOBS: readonly HubJob[] = ['foundation', 'crawl', 'ads'];
const PROVE_GATED_SCREENS: readonly OnboardingScreen[] = ['foundation', 'crawl', 'ads'];

/** Pull a human detail line out of an axios-style error, if the server sent one. */
function detailOf(err: unknown): string | null {
  if (err && typeof err === 'object' && 'response' in err) {
    const detail = (err as { response?: { data?: { detail?: unknown } } }).response?.data
      ?.detail;
    if (typeof detail === 'string' && detail.trim()) return detail;
  }
  return null;
}

export default function OnboardingPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { step: stepParam } = useParams<{ step?: string }>();
  const tenantId = useTenantStore((s) => s.currentTenant) ?? '';
  const workspace = useWorkspaceStore((s) => s.currentWorkspace);
  const selectWorkspace = useWorkspaceStore((s) => s.selectWorkspace);
  const setWorkspaces = useWorkspaceStore((s) => s.setWorkspaces);

  const draftKey = tenantId || PRE_TENANT_KEY;
  const stored = useOnboardingStore((s) => s.draftByKey[draftKey]);
  const draft = stored ?? emptyDraft();
  const patchDraft = useOnboardingStore((s) => s.patchDraft);
  const completeJob = useOnboardingStore((s) => s.completeJob);
  const adoptTenant = useOnboardingStore((s) => s.adoptTenant);
  const clearKey = useOnboardingStore((s) => s.clearKey);

  const [busy, setBusy] = useState(false);

  // Server is source of truth once a business exists. Never called pre-tenant.
  const { data: status, isLoading } = useQuery({
    queryKey: ['onboarding-status', tenantId],
    queryFn: () => fetchOnboardingStatus(tenantId),
    enabled: Boolean(tenantId),
    staleTime: 60_000,
  });

  const refreshStatus = useCallback(
    (key: string) =>
      queryClient.invalidateQueries({ queryKey: ['onboarding-status', key] }),
    [queryClient],
  );

  // Hydrate the draft cache from server status. foundationDone stays local —
  // the status payload does not carry it.
  useEffect(() => {
    if (!tenantId || !status) return;
    patchDraft(tenantId, {
      package: status.package ?? draft.package,
      completedJobs: status.completedJobs,
      justMe: status.justMe,
      siteUrl: status.siteUrl ?? draft.siteUrl,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, status, patchDraft]);

  const screen = resolveScreen(stepParam, draft.package);
  const midFlow =
    Boolean(draft.package) || draft.completedJobs.length > 0 || Boolean(draft.businessName);
  const proveDone = draft.completedJobs.includes('prove');
  // Direct-URL guard: a prove-gated card without proof shows the hub instead.
  const effectiveScreen: OnboardingScreen =
    !proveDone && PROVE_GATED_SCREENS.includes(screen) ? 'hub' : screen;

  const goTo = useCallback(
    (next: OnboardingScreen) => {
      if (next === 'gate' || next === 'hub') {
        navigate('/onboarding');
        return;
      }
      navigate(`/onboarding/${next}`);
    },
    [navigate],
  );

  const patch = useCallback(
    (next: Partial<typeof draft>) => patchDraft(draftKey, next),
    [draftKey, patchDraft],
  );

  const openJob = useCallback(
    (job: HubJob) => {
      if (PROVE_GATED_JOBS.includes(job) && !proveDone) {
        toast.error('Prove the site first — this unlocks after the site answers.');
        return;
      }
      goTo(firstScreenForJob(job));
    },
    [goTo, proveDone],
  );

  // Go-live is the server's call — it 409s when a required job is still open.
  const finish = useCallback(async () => {
    if (!tenantId) {
      toast.error('Create your business first.');
      return;
    }
    setBusy(true);
    try {
      await completeOnboarding(tenantId);
      clearKey(draftKey);
      navigate('/overview');
    } catch (err: unknown) {
      toast.error(detailOf(err) || 'A required job is still open on your list.');
    } finally {
      setBusy(false);
    }
  }, [clearKey, draftKey, navigate, tenantId]);

  const confirmSetup = useCallback(async () => {
    setBusy(true);
    try {
      let key = tenantId;
      if (!key) {
        const created = await createBusiness(draft.businessName, draft.domain);
        selectWorkspace(created);
        const list = await fetchWorkspaces().catch(() => [created]);
        setWorkspaces(list.length ? list : [created]);
        key = normalizeWebsite(created.website || draft.domain);
        adoptTenant(key);
      }
      if (!key) throw new Error('Business has no website key');
      // Contract order: package write, then setup (name + locations). The
      // server marks the setup job. A pasted plugin key is saved into Core
      // here as a convenience (Core DB write only — NOT proof; prove-site
      // does the retrieval that actually proves the connection).
      if (draft.package) await savePackage(key, draft.package);
      await saveSetup(key, draft.businessName, draft.locations);
      if (draft.pluginKey.trim()) {
        try {
          await savePluginKey(key, draft.pluginKey);
        } catch {
          toast.error(
            "We saved your business. The plugin key didn't take — paste it again when you prove the site.",
          );
        }
      }
      completeJob(key, 'setup');
      await refreshStatus(key);
      navigate('/onboarding');
    } catch (err: unknown) {
      toast.error(detailOf(err) || "We couldn't create that just now. Try again.");
    } finally {
      setBusy(false);
    }
  }, [
    adoptTenant,
    completeJob,
    draft.businessName,
    draft.domain,
    draft.locations,
    draft.package,
    draft.pluginKey,
    navigate,
    refreshStatus,
    selectWorkspace,
    setWorkspaces,
    tenantId,
  ]);

  const sendInvite = useCallback(
    async (email: string) => {
      const workspaceId = workspace?.id;
      if (!workspaceId) {
        toast.error('Create your business first.');
        return;
      }
      setBusy(true);
      try {
        await inviteTeammate(workspaceId, email);
      } catch {
        toast.error("That invite didn't send. Check the address and try again.");
        return;
      } finally {
        setBusy(false);
      }
      const jobs = draft.completedJobs.includes('invite')
        ? draft.completedJobs
        : [...draft.completedJobs, 'invite' as const];
      patch({ inviteEmails: [...draft.inviteEmails, email.trim()] });
      completeJob(draftKey, 'invite');
      toast.success(`Invite sent to ${email.trim()}`);
      if (tenantId) {
        patchProgress(tenantId, { completedJobs: jobs })
          .then(() => refreshStatus(tenantId))
          .catch(() =>
            toast.error("Invite sent, but the tick didn't save to the server."),
          );
      }
    },
    [
      completeJob,
      draft.completedJobs,
      draft.inviteEmails,
      draftKey,
      patch,
      refreshStatus,
      tenantId,
      workspace?.id,
    ],
  );

  const justMe = useCallback(async () => {
    patch({ justMe: true });
    completeJob(draftKey, 'invite');
    if (tenantId) {
      const jobs = draft.completedJobs.includes('invite')
        ? draft.completedJobs
        : [...draft.completedJobs, 'invite' as const];
      try {
        await patchProgress(tenantId, { justMe: true, completedJobs: jobs });
        await refreshStatus(tenantId);
      } catch {
        toast.error("Saved here, but it didn't reach the server. Try again.");
        return;
      }
    }
    goTo('hub');
  }, [completeJob, draft.completedJobs, draftKey, goTo, patch, refreshStatus, tenantId]);

  const prove = useCallback(async () => {
    if (!tenantId) {
      toast.error('Create your business first.');
      return;
    }
    if (!draft.pluginKey.trim() && !status?.wpKeyPresent) {
      toast.error('Paste the plugin key first.');
      return;
    }
    setBusy(true);
    try {
      // One checked call: saves the key (Core DB only) and does an
      // authenticated GET against the site. Pass = the retrieval succeeds;
      // no WordPress content is created.
      const result = await proveSite(tenantId, draft.pluginKey || undefined);
      patch({ siteUrl: result.site_url ?? draft.siteUrl });
      completeJob(draftKey, 'prove');
      await refreshStatus(tenantId);
      toast.success('The site answered — connection proven.');
    } catch {
      toast.error("The site didn't answer. The key may be wrong, or WordPress is unreachable.");
    } finally {
      setBusy(false);
    }
  }, [
    completeJob,
    draft.pluginKey,
    draft.siteUrl,
    draftKey,
    patch,
    refreshStatus,
    status?.wpKeyPresent,
    tenantId,
  ]);

  const toggleFoundation = useCallback(
    (id: string) => {
      const has = draft.foundationDone.includes(id);
      const next = has
        ? draft.foundationDone.filter((x) => x !== id)
        : [...draft.foundationDone, id];
      patch({ foundationDone: next });
      if (tenantId) {
        patchProgress(tenantId, { foundationDone: next }).catch(() =>
          toast.error("That tick didn't save. Tap it again."),
        );
      }
    },
    [draft.foundationDone, patch, tenantId],
  );

  const markJob = useCallback(
    async (job: HubJob) => {
      completeJob(draftKey, job);
      if (tenantId) {
        const jobs = draft.completedJobs.includes(job)
          ? draft.completedJobs
          : [...draft.completedJobs, job];
        try {
          await patchProgress(tenantId, { completedJobs: jobs });
          await refreshStatus(tenantId);
        } catch {
          toast.error("Saved here, but it didn't reach the server. Try again.");
          return;
        }
      }
      goTo('hub');
    },
    [completeJob, draft.completedJobs, draftKey, goTo, refreshStatus, tenantId],
  );

  const setupHandlers = useMemo(
    () => ({
      patch,
      goInfo: () => goTo('setup-info'),
      goArea: () => goTo('setup-area'),
      goWebsite: () => goTo('setup-website'),
      goReview: () => goTo('setup-review'),
      goHub: () => goTo('hub'),
    }),
    [goTo, patch],
  );

  // Existing client with a bible row, not mid-flow — this panel is not for them.
  if (tenantId && isLoading) {
    return (
      <OnboardingFrame>
        <StepBody>
          <Eyebrow>One moment</Eyebrow>
          <Lede>Getting things ready…</Lede>
        </StepBody>
      </OnboardingFrame>
    );
  }

  // Existing client, not mid-flow — this panel is not for them. Contract rule:
  // already_on_file && completed. Fallback for a backend that does not send
  // `completed` yet: the bible row alone excludes (already_on_file &&
  // !midFlow). already_on_file alone is NOT enough when completed is known —
  // setup creates the bible row, so an in-progress user would bounce wrongly.
  if (status && !midFlow) {
    const excluded =
      status.completed === undefined
        ? status.alreadyOnFile
        : status.alreadyOnFile && status.completed;
    if (excluded) {
      return <Navigate to="/overview" replace />;
    }
  }

  return (
    <OnboardingFrame>
      {effectiveScreen === 'gate' && (
        <PackageGate
          selected={draft.package}
          onSelect={(pkg) => patch({ package: pkg })}
        />
      )}
      {effectiveScreen === 'hub' && (
        <Hub
          package={draft.package}
          completed={draft.completedJobs}
          businessName={draft.businessName || workspace?.name || ''}
          welcomeBack={draft.completedJobs.length > 0}
          lockedJobs={draft.completedJobs.includes('prove') ? [] : ['foundation', 'crawl', 'ads']}
          onOpenJob={openJob}
          onGoLive={finish}
        />
      )}
      {effectiveScreen === 'setup-info' && (
        <BusinessInfo
          draft={draft}
          onChange={setupHandlers.patch}
          onNext={setupHandlers.goArea}
          onBack={setupHandlers.goHub}
        />
      )}
      {effectiveScreen === 'setup-area' && (
        <ServiceArea
          draft={draft}
          onChange={setupHandlers.patch}
          onNext={setupHandlers.goWebsite}
          onBack={setupHandlers.goInfo}
        />
      )}
      {effectiveScreen === 'setup-website' && (
        <Website
          draft={draft}
          onChange={setupHandlers.patch}
          onNext={setupHandlers.goReview}
          onBack={setupHandlers.goArea}
        />
      )}
      {effectiveScreen === 'setup-review' && (
        <Review
          draft={draft}
          busy={busy}
          onConfirm={confirmSetup}
          onBack={setupHandlers.goWebsite}
        />
      )}
      {effectiveScreen === 'invite' && (
        <InviteTeam
          draft={draft}
          busy={busy}
          onSend={sendInvite}
          onJustMe={justMe}
          onHub={setupHandlers.goHub}
        />
      )}
      {effectiveScreen === 'prove' && (
        <ProveSite
          draft={draft}
          savedKeyPresent={Boolean(status?.wpKeyPresent)}
          busy={busy}
          onChange={patch}
          onProve={prove}
          onHub={setupHandlers.goHub}
        />
      )}
      {effectiveScreen === 'foundation' && (
        <Foundation
          draft={draft}
          onToggle={toggleFoundation}
          onDone={() => markJob('foundation')}
          onHub={setupHandlers.goHub}
        />
      )}
      {effectiveScreen === 'crawl' && (
        <BaselineCrawl onDone={() => markJob('crawl')} onHub={setupHandlers.goHub} />
      )}
      {effectiveScreen === 'ads' && (
        <AdsLink onDone={() => markJob('ads')} onHub={setupHandlers.goHub} />
      )}
    </OnboardingFrame>
  );
}
